// The Reports screen's window state, in the two shapes it has to exist in: the
// browser URL and the query string sent to /api/reports (docs/REPORTS-TAB-DESIGN.md
// §3.2). The sibling of web/src/components/tickets/queue-query.js, and deliberately
// the same shape, so the app has one idea about filter state rather than two.
//
// They are close but not identical, and the difference is the point:
//
//   THE ADDRESS BAR keeps a preset as a preset. `?range=7d` still means "the last
//   seven days" when the link is opened tomorrow. Freezing it to explicit dates at
//   share time would hand someone a link that silently means a different week than
//   the one the sender was looking at.
//
//   THE API CALL is always explicit `from`/`to`. The server would otherwise have to
//   own a second definition of "7d", and two implementations of a window is how a
//   report and its own caption end up disagreeing.
//
// Only documented params are emitted. An unrecognised one is 400 INVALID_QUERY_PARAM
// on purpose (docs/API.md §1.6).

import { supportPath } from '../../support-path.js';

/** The three reports, in sidebar order. `id` is the URL segment. */
export const REPORTS = [
  { id: 'volume', label: 'Volume and mix', path: supportPath('/reports/volume') },
  { id: 'first-reply', label: 'First reply', path: supportPath('/reports/first-reply') },
  { id: 'backlog', label: 'Backlog', path: supportPath('/reports/backlog') },
];

export const DEFAULT_REPORT = 'volume';

/**
 * The fourth sidebar entry, and deliberately NOT in `REPORTS`.
 *
 * `REPORTS` is the list of FIXED reports: every entry's `id` is a path segment under
 * /api/reports that the shell fetches with `toApiQuery`, and every entry has a panel.
 * The builder has neither — it owns its own request and its own window state — so adding
 * it to that array would make the shell ask for `/api/reports/builder` and 404. It is a
 * separate constant for the same reason `isReportId` still answers false for it.
 */
export const BUILDER = { id: 'builder', label: 'Build a report', path: supportPath('/reports/builder') };

export function isReportId(id) {
  return REPORTS.some((report) => report.id === id);
}

export const RANGE_PRESETS = ['7d', '30d', '90d', 'custom'];

// SEVEN, not thirty. MEASURED on this database: the oldest ticket is 23 Aug 2026, so
// a 30-day window is 26 rows of "we have no record" and 4 of data. A wall of em
// dashes reads as broken software rather than as a young desk. Revisit when the
// horizon (meta.data_from) covers a month of real traffic — SPEC §9 defers historical
// migration until after cutover, so that may be months away.
export const DEFAULT_RANGE = '7d';

const PRESET_DAYS = { '7d': 7, '30d': 30, '90d': 90 };

// Must match src/routes/api/reports.js TIMEZONES exactly — the server rejects
// anything else with a 400, and MEASURED, an unknown zone name is a hard SQL error
// rather than an empty result, which is why it is an enum on both sides.
export const TIMEZONES = ['Pacific Standard Time', 'UTC'];
export const DEFAULT_TIMEZONE = 'Pacific Standard Time';

const MS_PER_DAY = 86400000;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function dayToMs(day) {
  return Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
}

/** A real calendar date, not merely a well-shaped string: '2026-02-31' is not one. */
export function isValidDay(day) {
  if (typeof day !== 'string' || !DATE_PATTERN.test(day)) return false;
  const back = new Date(dayToMs(day));
  return back.toISOString().slice(0, 10) === day;
}

/**
 * A preset and a reference date to an explicit inclusive window.
 *
 * `today` is an ARGUMENT rather than a `new Date()` call inside, and that is the
 * entire reason this function is testable. It is a 'YYYY-MM-DD' string.
 *
 * The window ENDS today and includes it, so '7d' is today plus the six days before
 * it — not eight days, and not seven days ending yesterday.
 *
 * Arithmetic is in Date.UTC space so a DST boundary inside the window cannot move an
 * endpoint by a day.
 */
export function resolvePreset(preset, today) {
  const days = PRESET_DAYS[preset];
  if (!days || !isValidDay(today)) return null;
  return {
    from: new Date(dayToMs(today) - (days - 1) * MS_PER_DAY).toISOString().slice(0, 10),
    to: today,
  };
}

/**
 * URLSearchParams to the shape the range control and both serialisers read.
 *
 * An unparseable value falls back to the default rather than throwing: this is an
 * address bar, and a hand-edited one should land on a working report rather than on
 * an error surface.
 */
export function readReportParams(searchParams) {
  const rawRange = searchParams.get('range') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const tz = searchParams.get('tz') ?? '';
  const queueId = searchParams.get('queue_id') ?? '';

  // An explicit, valid pair of dates IS a custom range whether or not `range=custom`
  // was spelled out, so a link carrying only from/to opens on what it names.
  const hasCustomDates = isValidDay(from) && isValidDay(to) && from <= to;
  let range = RANGE_PRESETS.includes(rawRange) ? rawRange : '';
  if (range === '') range = hasCustomDates ? 'custom' : DEFAULT_RANGE;

  // An explicit `range=custom` SURVIVES having no usable dates yet. It has to: the
  // moment someone picks "Custom" from the range control there are no dates, and
  // falling back to the default here would snap the control straight back to
  // "Last 7 days" — a picker that cannot be moved. With the range kept, the two date
  // inputs appear, `effectiveWindow` returns null, and the screen asks for the dates
  // instead of quietly reporting a different week than the URL claims.
  //
  // Each date is kept only if it is a real calendar day, INDEPENDENTLY, so a
  // half-entered range keeps the half that was entered.
  return {
    range,
    from: range === 'custom' && isValidDay(from) ? from : '',
    to: range === 'custom' && isValidDay(to) ? to : '',
    tz: TIMEZONES.includes(tz) ? tz : DEFAULT_TIMEZONE,
    queueId: /^\d+$/.test(queueId) ? queueId : '',
  };
}

/**
 * What goes in the address bar: shareable, and free of redundant defaults.
 *
 * A preset stays a preset — see the header. Defaults are omitted so an untouched
 * report has a clean address.
 */
export function toSearchParams(params) {
  const search = new URLSearchParams();
  if (params.range === 'custom') {
    search.set('range', 'custom');
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
  } else if (params.range && params.range !== DEFAULT_RANGE) {
    search.set('range', params.range);
  }
  if (params.tz && params.tz !== DEFAULT_TIMEZONE) search.set('tz', params.tz);
  if (params.queueId) search.set('queue_id', params.queueId);
  return search;
}

/**
 * What goes to the API: ALWAYS explicit from/to, so the server never has to own a
 * second definition of "the last 7 days".
 *
 * `today` is passed in for the same reason `resolvePreset` takes it.
 *
 * `report` decides which params are legal: /backlog is a census at one instant and
 * accepts no window at all, and sending it one would be 400 INVALID_QUERY_PARAM
 * because an unrecognised param name is a global refusal.
 */
export function toApiQuery(params, today, report = DEFAULT_REPORT) {
  const search = new URLSearchParams();

  if (report !== 'backlog') {
    const window = params.range === 'custom'
      ? { from: params.from, to: params.to }
      : resolvePreset(params.range, today) ?? resolvePreset(DEFAULT_RANGE, today);
    if (window && isValidDay(window.from) && isValidDay(window.to)) {
      search.set('from', window.from);
      search.set('to', window.to);
    }
  }

  // Sent even when it equals the default. Unlike the address bar, the wire should
  // say which zone a number was bucketed in — `meta.window.timezone` is echoed back
  // and printed on the screen, and a caption that reads "Eastern" because both sides
  // happened to default the same way is a caption that stops being true the day one
  // of them changes.
  if (params.tz) search.set('tz', params.tz);
  if (params.queueId) search.set('queue_id', params.queueId);
  return search.toString();
}

/**
 * The window a report is ACTUALLY going to ask for, for the caption above it.
 * Returns null when a custom range is half-entered — the caller shows nothing rather
 * than a window it has invented.
 */
export function effectiveWindow(params, today) {
  if (params.range === 'custom') {
    return isValidDay(params.from) && isValidDay(params.to) && params.from <= params.to
      ? { from: params.from, to: params.to }
      : null;
  }
  return resolvePreset(params.range, today);
}
